// search-layer/services/search.service.ts
import { BaseService } from '../../../common/http-layer/service/base.service'
import type { SearchParams, SearchResultItem } from '../types/search'

/**
 * Search API under `REST_API_URLS` (clientportal + shared apps).
 * `basePath: /communities` matches the BFF/gateway for learner search (not `/gokollab/communities`, which is the standalone app mount).
 */
export class SearchService extends BaseService {
  constructor() {
    super({
      baseURL: 'REST_API_URLS',
      basePath: '/communities',
    })
  }

  /**
   * Search within a specific group
   * @param locationId - Location ID
   * @param groupId - Group ID
   * @param query - Search query string
   * @param params - Additional search parameters (includes token for pagination)
   * @returns Search results
   */
  async searchInGroup(locationId: string, groupId: string, query: string, params?: Partial<SearchParams>): Promise<SearchResultItem[]> {
    const { data } = await this.get<SearchResultItem[]>(`/${locationId}/groups/${groupId}/search`, {
      params: {
        query,
        ...params,
      },
      locationId,
      groupId,
    })
    return data
  }

  /**
   * Search within a specific channel
   * @param locationId - Location ID
   * @param groupId - Group ID
   * @param channelId - Channel ID
   * @param query - Search query string
   * @param params - Additional search parameters (includes token for pagination)
   * @returns Search results
   */
  async searchInChannel(
    locationId: string,
    groupId: string,
    channelId: string,
    query: string,
    params?: Partial<SearchParams>
  ): Promise<SearchResultItem[]> {
    const { data } = await this.get<SearchResultItem[]>(`/${locationId}/groups/${groupId}/channels/${channelId}/search`, {
      params: {
        query,
        ...params,
      },
      locationId,
      groupId,
    })
    return data
  }

  /**
   * Search across all groups in a location
   * @param locationId - Location ID
   * @param query - Search query string
   * @param params - Additional search parameters
   * @returns Search results
   */
  async searchInLocation(locationId: string, query: string, params?: Partial<SearchParams>): Promise<SearchResultItem[]> {
    const { data } = await this.get<SearchResultItem[]>(`/${locationId}/search`, {
      params: {
        query,
        ...params,
      },
      locationId,
    })
    return data
  }

  /**
   * Generic search method that adapts based on context
   * @param searchParams - Search parameters including context
   * @returns Search results
   */
  async search(searchParams: SearchParams): Promise<SearchResultItem[]> {
    const { locationId, groupId, channelId, query, ...rest } = searchParams

    if (!query || query.trim().length === 0) {
      return []
    }

    // Channel-specific search
    if (channelId && groupId && locationId) {
      return this.searchInChannel(locationId, groupId, channelId, query, rest)
    }

    // Group-specific search
    if (groupId && locationId) {
      return this.searchInGroup(locationId, groupId, query, rest)
    }

    // Location-wide search
    if (locationId) {
      return this.searchInLocation(locationId, query, rest)
    }

    throw new Error('locationId is required for search')
  }
}
